import React from "react";
import {
  useGetTripTypesQuery,
  useGetCountriesQuery,
  useGetReservationsQuery,
} from "../../redux/Service/api";
import fallBackImage from "../../assets/Bookings1.png";
import { useNavigate } from "react-router-dom";

interface Service {
  ReserveID: number;
  TripTypeID: number;
  TripNo: string;
  TripDate: string;
  CountryFromID: number;
  CountryToID: number;
  Notes: string;
}

interface TripType {
  TripTypeID: number;
  TripTypeName: string;
}

interface Country {
  CountryID: number;
  CountryName: string;
}

const AllServices = () => {
  const navigate = useNavigate();
  // Fetch services
  const {
    data: allServicesData,
    isError: isServicesError,
    isLoading: isServicesLoading,
  } = useGetReservationsQuery(undefined);

  // Fetch trip types
  const {
    data: tripTypesData,
    isError: isTripTypesError,
    isLoading: isTripTypesLoading,
  } = useGetTripTypesQuery(undefined);

  // Fetch countries
  const {
    data: countriesData,
    isError: isCountriesError,
    isLoading: isCountriesLoading,
  } = useGetCountriesQuery(undefined);

  const services: Service[] = allServicesData?.ResponseObject ?? [];
  const tripTypes: TripType[] = tripTypesData?.ResponseObject ?? [];
  const countries: Country[] = countriesData?.ResponseObject ?? [];

  // Match TripTypeID with TripTypeName
  const getTripTypeName = (tripTypeID: number) =>
    tripTypes.find((type) => type.TripTypeID === tripTypeID)?.TripTypeName ??
    "غير معروف";

  // Match CountryID with CountryName
  const getCountryName = (countryID: number) =>
    countries.find((country) => country.CountryID === countryID)?.CountryName ??
    "غير معروف";

  if (isServicesLoading || isTripTypesLoading || isCountriesLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">جاري تحميل البيانات...</p>
      </div>
    );
  }

  if (isServicesError || isTripTypesError || isCountriesError) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500">فشل في تحميل البيانات. حاول مرة أخرى.</p>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500">لا توجد خدمات متوفرة حاليًا.</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-10 text-right" dir="rtl">
      {/* Section Title */}
      <h2 className="text-center text-2xl md:text-3xl lg:text-4xl font-bold text-gray-800 mb-8">
        جميع الخدمات
      </h2>

      {/* Services List */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
        {services.map((service) => (
          <div
            onClick={() => navigate(`/ReservationDetails/${service.ReserveID}`)}
            key={service.ReserveID}
            className="p-6 bg-white cursor-pointer rounded-xl shadow-lg border border-gray-200 hover:shadow-2xl transition-shadow duration-300"
          >
            <h3 className="text-lg font-semibold text-blue-600 mb-3">
              {service.TripNo}
            </h3>
            <div className="mb-3">
              <p className="text-sm text-gray-700 mb-1">
                <strong className="text-gray-800">نوع الخدمة:</strong>{" "}
                {getTripTypeName(service.TripTypeID)}
              </p>
              <p className="text-sm text-gray-700 mb-1">
                <strong className="text-gray-800">تاريخ الخدمة:</strong>{" "}
                {new Date(service.TripDate).toLocaleDateString("ar-EG")}
              </p>
              <p className="text-sm text-gray-700 mb-1">
                <strong className="text-gray-800">من البلد:</strong>{" "}
                {getCountryName(service.CountryFromID)}
              </p>
              <p className="text-sm text-gray-700 mb-1">
                <strong className="text-gray-800">إلى البلد:</strong>{" "}
                {getCountryName(service.CountryToID)}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllServices;
